import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import useFetchWrapper, { HttpResponse } from '../../Helpers/useFetchWrapper';
import useObjectHelper from '../../Helpers/useObjectHelper';

import {
	ResourceFormData,
	Props,
	Participant,
	UsersResponse,
} from './CreateResource.model';
import {useAuthenticationDataContext} from "../../Helpers/AuthenticationDataProvider";
import { useAuthorizationDataContext } from '../../Helpers/AuthorizationDataProvider';

const useCreateResource = ({ author, onClose, recordingId }: Props) => {
	const [ file, setFile ] = useState<any>(null);
	const [ members, setMembers ] = useState<UsersResponse[]>([]);
	const [ participants, setParticipants ] = useState<UsersResponse[]>([]);
	const [isAdminAuthor, setIsAdminAuthor] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [fileTooLarge, setFileTooLarge] = useState<boolean>(false);
	const [resourceId, setResourceId] = useState<string>();
	const [resourceUrl, setResourceUrl] = useState<string>();
	
	const defaultValues: any = {
		acceptsComments: false,
		author: author ? author.userId : null,
		description: '',
		isForApplicantMentees: false,
		researchFields: [],
		title: '',
	};

	const { control, formState: { errors }, handleSubmit } = useForm({ defaultValues });

	const { get, post, postFormData } = useFetchWrapper();
	const { objToFormData } = useObjectHelper();
	const { userId } = useAuthenticationDataContext();
	const { isAuthorized, role } = useAuthorizationDataContext();

	useEffect(() => {
		getMembersList();
	}, []);

	useEffect(() => {
		isFileTooLarge();
	}, [file]);

	const isFileTooLarge = () => {
		if (file && file.size) {
			let fileSizeGb = file?.size / 1024 / 1024 /1024;
			setFileTooLarge(fileSizeGb > 1);
		}
	};
 
	const getMembersList = async () => {
		const response: HttpResponse<UsersResponse[]> = await get(`/user/all-types`);
		response.parsedBody && setMembers(response.parsedBody);
	}

	const addParticipant = (participant: Participant) => {
		// Remove person from network and add it to participants
		setMembers(members.filter((item) => item.userId !== participant.userId));
		setParticipants([
			...participants,
			participant,
		]);
	}

	const onChangeFile = (info: any) => {
		setFile(info.file.originFileObj);
	}

	const getFileType = (file: File) => {
		const { name, type } = file;
		return {format: name.substring(name.lastIndexOf('.'), name.length) || name, name: name.substring(0, name.lastIndexOf('.')),type};
	}

	const getResourceUrl = async () => {
		const { format, name } = getFileType(file);
		console.log(file);
		const response: HttpResponse<{id:string, url: string}> = await post('/resource/personal/url', { format, title: name });
		if (response.parsedBody) {
			const { id, url } = response.parsedBody;
			return { id, url };
		}
		return;
	}

	const uploadToS3 = async (url: string) => {
		const { type } = getFileType(file);
		await fetch(url, { body: file, headers: { 'Content-Type': type }, method: "PUT" });
	}

	const onCreateResource = async (data: ResourceFormData) => {
		!loading && setLoading(true);
		const {
			acceptsComments,
			author,
			description,
			isForApplicantMentees,
			researchFields,
			title,
		} = data;

		if (recordingId) {
			const body = {
				acceptsComments,
				author,
				description,
				isForApplicantMentees,
				participants: participants.map(item => item.userId),
				recordingId: recordingId,
				researchFields: researchFields,
				title,
			};
			await post('/resource/recording', body);
			setLoading(false);
			onClose();
			
		} else {
			const S3Data = await getResourceUrl();
			if (S3Data) {
				const { id, url } = S3Data;
				const { format } = getFileType(file);
				await uploadToS3(url);
				const body = {
					acceptsComments,
					author: isAdminAuthor ? userId : author,
					description,
					format: format,
					id: id,
					isForApplicantMentees,
					participants: participants.map(item => item.userId),
					researchFields: researchFields,
					title,
				};
				await post(`/resource/personal`, body);
				setLoading(false);
				onClose();	
			}
		}
	}

	const removeParticipant = (participant: Participant) => {
		// Remove person from participants and add it to network
		setParticipants(participants.filter((item) => item.userId !== participant.userId));
		setMembers([
			...members,
			participant,
		]);
	}

	const canAddApplicantMentees = () => {
		return isAuthorized(['ViewApplicantMentees']) || role === 'Admin';
	}

	return {
		addParticipant,
		canAddApplicantMentees,
		control,
		defaultValues,
		errors,
		handleSubmit,
		onChangeFile,
		onCreateResource,
		removeParticipant,
		setIsAdminAuthor,
		state: {
			file,
			fileTooLarge,
			isAdminAuthor,
			loading,
			members,
			participants,
		},
	}
}

export default useCreateResource;